import React, { useEffect, useState } from 'react'
import { View, ScrollView, TouchableWithoutFeedback } from 'react-native'
import {
  Iffy,
  Page,
  responsive,
  util,
} from '@alucio/lux-ui'
import ROUTES from 'src/router/routeDef'
import LoadingErrorMessage from '../components/ErrorMessages/LoadingErrorMessage';
import DNALoader, { DNALoaderEvents } from 'src/components/DNA/Loader/DNALoader'
import DNASlideTransition from 'src/components/DNA/SlideTransition/DNASlideTransition';
import logger from 'src/utils/logger';
import DeviceDebug from './DeviceDebug';

const S = {
  Page: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  PageMobile: {
    justifyContent: 'center',
  },
  LoaderImage: {
    width: 200,
    height: 200,
  },
  Debug: {
    flex: 1,
    maxWidth: '100dvw'
  }
}

/**
 * This hook is used to determine if the current window location is for an external player.
 *
 * Note: We cannot use React Router here because this hook is used before the Router component is initialized.
 *
 * @returns {boolean} - Returns true if the current window location is for an external player, otherwise false.
 */
export const useIsExternalPlayer = () => {
  const pathToken = ROUTES
    .MEETING_CONTENT
    .PATH
    .split('/')
    .filter(token => token)
    .at(0)

  const externalWindow = pathToken
    ? window.location.pathname.includes(pathToken)
    : false

  return !!externalWindow;
}

interface Props {
  showRefreshComponent?: boolean
  analyticsEventType?: DNALoaderEvents
  context?: string
  useSubtleLoadingIndicator?: boolean
}

const Loading = (props: Props) => {
  const { showRefreshComponent, analyticsEventType, context, useSubtleLoadingIndicator = false } = props;
  const [showLongTimeLoadingMessage, setShowLongTimeLoadingMessage] = useState<boolean>(false);
  const [showDebug, setShowDebug] = useState<boolean>(false)

  useEffect(() => {
    const handle = setTimeout(() => {
      logger.appInit.info('Setting LONG_HYDRATE', { context })
      analytics?.track('APP_LONG_HYDRATE', {
        action: 'LONG_HYDRATE',
        category: 'APP',
      });
      setShowLongTimeLoadingMessage(true);
    }, 25000)
    return () => {
      clearTimeout(handle)
    }
  }, [])

  if (showDebug)
    return (
      <ScrollView style={S.Debug} contentContainerStyle={S.Debug}>
        <DeviceDebug />
      </ScrollView>
    )

  return (
    <Page
      style={util.mergeStyles(
        undefined,
        S.Page,
        [S.PageMobile, !responsive.device.isWeb],
      )}
    >
      <View style={{ justifyContent: 'center', alignItems: 'center' }}>
        <View style={{ height: 200 }}>
          {useSubtleLoadingIndicator
            ? <DNASlideTransition analyticsEventType={analyticsEventType} />
            : <DNALoader analyticsEventType={analyticsEventType} />}
        </View>
        <Iffy is={showLongTimeLoadingMessage || showRefreshComponent}>
          <TouchableWithoutFeedback onLongPress={() => setShowDebug(true)} delayLongPress={5000}>
            <LoadingErrorMessage hideLogoutOption={showRefreshComponent} />
          </TouchableWithoutFeedback>
        </Iffy>
      </View>
    </Page>
  );
}

export default Loading;
